// frontend/src/services/upload.service.ts
import api from './api';
import { UploadResponse, MultiUploadResponse } from '../types';

const UploadService = {
  /**
   * Upload a single image
   */
  uploadImage: async (file: File, folder?: string): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (folder) {
        formData.append('folder', folder);
      }

      const response = await api.post<UploadResponse>('/uploads/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Upload response:', response.data);
      
      // Check if response.data is a string (direct URL)
      if (typeof response.data === 'string' && isValidUrl(response.data)) {
        return response.data;
      }
      
      // Check if response.data is an object with url property as string
      if (response.data && typeof response.data.url === 'string' && isValidUrl(response.data.url)) {
        return response.data.url;
      }
      
      // Check for nested url object format (what you're getting)
      if (response.data && 
          typeof response.data.url === 'object' && 
          response.data.url && 
          typeof response.data.url.url === 'string' && 
          isValidUrl(response.data.url.url)) {
        return response.data.url.url;
      }
      
      // Check for plain JSON object with url property (your specific case)
      if (response.data && typeof response.data === 'object') {
        // Try to extract direct URL from any property that might contain it
        const possibleUrlProps = Object.values(response.data).filter(val => 
          typeof val === 'string' && isValidUrl(val)
        );
        
        if (possibleUrlProps.length > 0) {
          return possibleUrlProps[0];
        }
      }
      
      console.error('Could not extract valid URL from response:', response.data);
      throw new Error('Invalid URL format returned from server');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image';
      console.error('Upload image error:', error);
      throw new Error(errorMessage);
    }
  },

  /**
   * Upload multiple images
   */
  uploadMultipleImages: async (files: File[], folder?: string): Promise<string[]> => {
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      if (folder) {
        formData.append('folder', folder);
      }

      const response = await api.post<MultiUploadResponse>('/uploads/images/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Validate all URLs before returning
      if (!response.data.urls || !Array.isArray(response.data.urls)) {
        throw new Error('Invalid URLs array returned from server');
      }
      
      // Filter out any invalid URLs
      const validUrls = response.data.urls.filter(url => 
        url && typeof url === 'string' && isValidUrl(url)
      );
      
      return validUrls;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload multiple images';
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete an image
   */
  deleteImage: async (url: string): Promise<boolean> => {
    try {
      if (!url || typeof url !== 'string' || !isValidUrl(url)) {
        throw new Error('Invalid URL provided for deletion');
      }
      
      const response = await api.delete<{ success: boolean }>('/uploads/images', {
        data: { url }
      });
      
      return response.data.success;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete image';
      throw new Error(errorMessage);
    }
  },

  /**
   * Optimizes Google Drive URLs for better image display
   * This method doesn't modify the original URLs stored in the database,
   * it only transforms them for display purposes.
   */
  getOptimizedImageUrl: (url: string): string => {
    if (!url || typeof url !== 'string') {
      return ''; // Return empty string for invalid URLs
    }
    
    try {
      // For existing Google Drive URLs, optimize them for display
      if (url.includes('drive.google.com')) {
        // Extract the ID from the URL
        let fileId = '';
        
        // Handle download URL format (export=download)
        if (url.includes('export=download')) {
          const idMatch = url.match(/id=([^&]+)/);
          fileId = idMatch ? idMatch[1] : '';
        }
        // Handle uc format
        else if (url.includes('drive.google.com/uc')) {
          const idMatch = url.match(/id=([^&]+)/);
          fileId = idMatch ? idMatch[1] : '';
        }
        // Handle file/d format
        else if (url.includes('file/d/')) {
          const idMatch = url.match(/file\/d\/([^/]+)/);
          fileId = idMatch ? idMatch[1] : '';
        }
        
        if (fileId) {
          // Use the thumbnail endpoint for display - this is more reliable
          return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
        }
      }
      
      // If it's not a Google Drive URL or can't extract ID, return the original
      return url;
    } catch (error) {
      console.error('Error optimizing URL:', error);
      return url; // Return original URL if optimization fails
    }
  },

  /**
   * Validates if a URL string is properly formatted
   */
  isValidUrl: (url: string): boolean => {
    return isValidUrl(url);
  }
};

/**
 * Helper function to check if a string is a valid URL
 */
function isValidUrl(urlString: string): boolean {
  try {
    new URL(urlString);
    return true;
  } catch (error) {
    return false;
  }
}

export default UploadService;